import { Navigate, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { Flex, IconButton, Skeleton, Text } from "@radix-ui/themes";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { Sample, useSamplingApi } from "@/v2/entities/sampling/Sampling.api";
import { useQuery } from "react-query";
import { useCallback, useMemo, useState } from "react";
import {
  ValidationRow,
  enhanceValidationRow,
  useSampleInteractions,
} from "@/v2/features/validation/validation.utils";
import { ValidationMetrics } from "@/v2/features/validation/ValidationMetrics";
import { InteractionsList } from "@/v2/features/validation/InteractionsList";
import { TicketViewContent } from "@/v2/features/interaction-view/TicketView";
import { ExploreInteractionRow } from "@/v2/entities/explore/Explore.model";
import { ConversationViewContent } from "@/v2/features/interaction-view/ConversationView";
import { SimulatorWrapper } from "@/routes/simulators/wrapper";

function useValidationId() {
  const { id } = useParams();
  return z.coerce.number().int().positive().parse(id);
}

function ValidationPageContent({ sample }: { sample: Sample }) {
  const navigate = useNavigate();

  const [selectedInteractionId, setSelectedInteractionId] = useState<string>();

  const {
    interactions: exploreInteractions,
    isLoading: isLoadingExploreInteractions,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch: refetchExploreInteractions,
  } = useSampleInteractions(sample);

  const validation = useMemo<ValidationRow | undefined>(() => {
    if (sample == null) {
      return;
    }

    const baseRow: ValidationRow = {
      id: sample.id,
      name: sample.name,
      scorecardName: sample.sampleConfiguration?.sampleForm?.name ?? "Unnamed",
      validationRules: sample.sampleConfiguration?.qaRules,
      total: sample.tickets.length + sample.conversations.length,
      dateFrom: sample.dateFrom,
      dateTo: sample.dateTo,
      sources: sample.sampleConfiguration?.sources,
      validated: "loading",
      avgQaScore: "loading",
      avgAutoQaScore: "loading",
    };

    if (isLoadingExploreInteractions) {
      return baseRow;
    }

    return enhanceValidationRow(baseRow, exploreInteractions);
  }, [sample, exploreInteractions, isLoadingExploreInteractions]);

  const selectedInteraction = useMemo<ExploreInteractionRow | undefined>(() => {
    return exploreInteractions.find((x) => x.id === selectedInteractionId);
  }, [exploreInteractions, selectedInteractionId]);

  const handleInteractionValidated = useCallback(() => {
    refetchExploreInteractions();
  }, [refetchExploreInteractions]);

  return (
    <Flex height="100%" direction="column" overflow="hidden">
      <Flex p="4" direction="column" gap="2" className="border-b">
        <Flex gap="3" align="center">
          <IconButton
            variant="soft"
            size="2"
            onClick={() => navigate("/quality/validations")}
          >
            <ArrowLeftIcon />
          </IconButton>

          <Text size="4" weight="medium" highContrast>
            {sample?.name}
          </Text>
        </Flex>

        <ValidationMetrics validation={validation} />
      </Flex>

      <Flex flexGrow="1" overflow="hidden" className="h-[0px]">
        <Flex className="w-[320px] border-r">
          <InteractionsList
            loading={isLoadingExploreInteractions}
            interactions={exploreInteractions}
            totalCount={sample.tickets.length + sample.conversations.length}
            selectedInteractionId={selectedInteractionId}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            onSelect={setSelectedInteractionId}
          />
        </Flex>
        <Flex
          flexGrow="1"
          direction="column"
          className={
            selectedInteraction == null ? "w-[0px] bg-muted" : "w-[0px]"
          }
          overflow="hidden"
        >
          {selectedInteraction != null && (
            <>
              {selectedInteraction.interactionType === "tickets" && (
                <SimulatorWrapper>
                  <TicketViewContent
                    ticket={selectedInteraction}
                    enrichments={[]}
                    customFields={[]}
                    allowSendToTraining={true}
                    allowValidate
                    onTicketValidated={handleInteractionValidated}
                  />
                </SimulatorWrapper>
              )}
              {selectedInteraction.interactionType === "conversations" && (
                <ConversationViewContent
                  conversation={selectedInteraction}
                  enrichments={[]}
                  allowSendToTraining={true}
                  allowValidate
                  onConversationValidated={handleInteractionValidated}
                />
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}

export function ValidationPage() {
  const navigate = useNavigate();
  const validationId = useValidationId();
  const { getSample } = useSamplingApi();

  const { data: sample, isLoading } = useQuery({
    queryKey: ["validations", validationId],
    queryFn: () => getSample(validationId),
  });

  if (isLoading) {
    return (
      <PageLayoutV2>
        <Flex height="100%" direction="column" overflow="hidden">
          <Flex p="4" direction="column" gap="2" className="border-b">
            <Flex gap="3" align="center">
              <IconButton
                variant="soft"
                size="2"
                onClick={() => navigate("/quality/validations")}
              >
                <ArrowLeftIcon />
              </IconButton>

              <Skeleton className="w-[160px] h-[26px] rounded-md" />
            </Flex>

            <ValidationMetrics />
          </Flex>

          <Flex flexGrow="1">
            <Flex className="w-[320px] border-r"></Flex>
            <Flex flexGrow="1" className="bg-muted"></Flex>
          </Flex>
        </Flex>
      </PageLayoutV2>
    );
  }

  if (sample == null) {
    return <Navigate to="/quality/validations" replace />;
  }

  return (
    <PageLayoutV2>
      <ValidationPageContent sample={sample} />
    </PageLayoutV2>
  );
}
